import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '../auth/AuthContext'
import { useIncidentStream } from '../hooks/useIncidentStream'

interface NavItem {
  to: string
  label: string
  permission?: string
}

const NAV: NavItem[] = [
  { to: '/', label: 'Дашборд' },
  { to: '/incidents', label: 'Инциденты', permission: 'alerts.read' },
  { to: '/routes', label: 'Маршрутизация', permission: 'policies.read' },
  { to: '/escalation', label: 'Эскалация', permission: 'policies.read' },
  { to: '/teams', label: 'Команды', permission: 'teams.read' },
  { to: '/schedules', label: 'Дежурства', permission: 'teams.read' },
  { to: '/integrations', label: 'Интеграции', permission: 'integrations.read' },
  { to: '/channels', label: 'Каналы', permission: 'integrations.read' },
  { to: '/audit', label: 'Аудит', permission: 'audit.read' },
]

/** Иконки рисуем сами: одна зависимость ради двух глифов не окупается. */
function MenuIcon() {
  return (
    <svg viewBox="0 0 20 20" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
    </svg>
  )
}

export function Layout() {
  const { me, logout, can, tenant } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [lastEvent, setLastEvent] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const status = useIncidentStream(
    useCallback((event) => setLastEvent(event.type), []),
  )

  // Переход по ссылке на телефоне обязан закрывать меню: иначе выбранный
  // раздел открывается под шторкой и выглядит так, будто ничего не произошло.
  useEffect(() => setMenuOpen(false), [location.pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const onLogout = async () => {
    await logout()
    navigate('/login')
  }

  const statusColor =
    status === 'online' ? 'bg-emerald-400' : status === 'connecting' ? 'bg-amber-400' : 'bg-red-500'

  return (
    <div className="flex h-full">
      {/* Затемнение под шторкой: гасит меню по нажатию мимо него. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-ink-800 border-r border-ink-600
          flex flex-col transition-transform duration-200
          lg:static lg:z-auto lg:w-56 lg:translate-x-0
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        aria-label="Разделы"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-4 border-b border-ink-600">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100">Alert Manager</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{tenant ?? '—'}</div>
          </div>
          <button
            className="btn-ghost !px-2 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label="Закрыть меню"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV.filter((item) => !item.permission || can(item.permission)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                // py-3 на телефоне: строка меню должна попадать под палец,
                // а не под курсор.
                `block px-4 py-3 text-sm transition-colors lg:py-2 ${
                  isActive
                    ? 'bg-ink-700 text-sky-300 border-l-2 border-sky-500'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-ink-700/60 border-l-2 border-transparent'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-3 border-t border-ink-600 text-xs text-slate-500">
          <div className="flex items-center gap-2" title={`realtime: ${status}`}>
            <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${statusColor}`} />
            <span className="truncate">
              {status === 'online' ? 'realtime подключён' : 'realtime не активен'}
            </span>
          </div>
          {lastEvent && <div className="mt-1 truncate">последнее: {lastEvent}</div>}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="shrink-0 bg-ink-800 border-b border-ink-600 flex items-center gap-3 px-4 py-2 lg:h-14 lg:px-6 lg:py-0">
          <button
            className="btn-ghost !px-2 lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Открыть меню"
            aria-expanded={menuOpen}
          >
            <MenuIcon />
          </button>

          {/* min-w-0 + truncate: длинная почта иначе распирает шапку и
              выталкивает кнопку выхода за край экрана. */}
          <div className="min-w-0 flex-1 text-sm text-slate-400">
            <div className="truncate">{me?.full_name || me?.email}</div>
            {me && !me.mfa_enabled && (
              <div className="text-xs text-amber-400 truncate">второй фактор не настроен</div>
            )}
          </div>

          <button className="btn-ghost shrink-0" onClick={onLogout}>
            Выйти
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 space-y-4 lg:p-6 lg:space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
